import { useState } from "react";
import type { TagInfo } from "../data/api";

import "./DrawingsDashboard.scss";

interface DrawingCardProps {
  id: string;
  name: string;
  thumbnail?: string | null;
  updatedAt: string | number;
  tags?: TagInfo[];
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (id: string) => void;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onRename: (id: string, newName: string) => void;
  onManageTags?: (id: string) => void;
  onMoveTo?: (id: string) => void;
  onShareViewLink?: (id: string) => void;
}

export const DrawingCard = ({
  id,
  name,
  thumbnail,
  updatedAt,
  tags = [],
  selectable = false,
  selected = false,
  onSelect,
  onOpen,
  onDelete,
  onDuplicate,
  onRename,
  onManageTags,
  onMoveTo,
  onShareViewLink,
}: DrawingCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(name);
  const [showMenu, setShowMenu] = useState(false);

  const handleRename = () => {
    if (editName.trim() && editName !== name) {
      onRename(id, editName.trim());
    }
    setIsEditing(false);
  };

  const formattedDate = new Date(updatedAt).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData("application/x-drawing-id", id);
    e.dataTransfer.effectAllowed = "move";
    const el = e.currentTarget as HTMLElement;
    el.classList.add("drawing-card--dragging");
  };

  const handleDragEnd = (e: React.DragEvent) => {
    const el = e.currentTarget as HTMLElement;
    el.classList.remove("drawing-card--dragging");
  };

  const handleClick = (e: React.MouseEvent) => {
    if (selectable && onSelect) {
      e.stopPropagation();
      onSelect(id);
    }
  };

  return (
    <div
      className={`drawing-card ${selected ? "drawing-card--selected" : ""} ${showMenu ? "drawing-card--menu-open" : ""}`}
      onDoubleClick={() => !selectable && onOpen(id)}
      onClick={handleClick}
      draggable={!selectable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {selectable && (
        <div className="drawing-card__checkbox">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect?.(id)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className="drawing-card__info">
        {isEditing ? (
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleRename();
              }
              if (e.key === "Escape") {
                setIsEditing(false);
                setEditName(name);
              }
            }}
            autoFocus
            className="drawing-card__name-input"
          />
        ) : (
          <span
            className="drawing-card__name"
            onDoubleClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
          >
            {name}
          </span>
        )}
        <span className="drawing-card__date">{formattedDate}</span>
        {tags.length > 0 && (
          <div className="drawing-card__tags">
            {tags.slice(0, 3).map((tag) => (
              <span
                key={tag.id}
                className="drawing-card__tag"
                style={{ backgroundColor: tag.color + "22", color: tag.color, borderColor: tag.color + "44" }}
              >
                {tag.name}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="drawing-card__tag drawing-card__tag--more">
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>

      {!selectable && (
        <div className="drawing-card__actions">
          <button
            className="drawing-card__menu-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            title="Menu"
          >
            &#8942;
          </button>
          {showMenu && (
            <div
              className="drawing-card__menu"
              onMouseLeave={() => setShowMenu(false)}
            >
              <button onClick={() => { onOpen(id); setShowMenu(false); }}>
                Abrir
              </button>
              <button
                onClick={() => {
                  setIsEditing(true);
                  setShowMenu(false);
                }}
              >
                Renomear
              </button>
              <button onClick={() => { onDuplicate(id); setShowMenu(false); }}>
                Duplicar
              </button>
              {onShareViewLink && (
                <button onClick={() => { onShareViewLink(id); setShowMenu(false); }}>
                  Link de visualizacao
                </button>
              )}
              {onMoveTo && (
                <button onClick={() => { onMoveTo(id); setShowMenu(false); }}>
                  Mover para...
                </button>
              )}
              {onManageTags && (
                <button onClick={() => { onManageTags(id); setShowMenu(false); }}>
                  Tags
                </button>
              )}
              <button
                className="danger"
                onClick={() => { onDelete(id); setShowMenu(false); }}
              >
                Excluir
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
